/**
 * Lista de espera: "se vagar alguma coisa depois das 17h, me chama".
 *
 * O pedido é uma **janela**, não um horário. Isso é o que faz um formato só
 * cobrir as duas frases que as pessoas realmente dizem:
 *
 *   "quando abrir algo depois das 17h"  → janela das 17h à meia-noite
 *   "assim que alguém cancelar, me avisa" → janela do dia inteiro
 *
 * Como todo o resto do core, aqui não há relógio, banco nem HTTP: a data de
 * hoje e os horários candidatos entram por parâmetro. É o que permite testar
 * "quem cabe na vaga das 17h30 de uma quinta" em microssegundos.
 *
 * A decisão de **quem** fica com a vaga não mora aqui e não mora em lugar
 * nenhum do código: é do barbeiro. Estas funções respondem apenas "esta pessoa
 * caberia?", que é uma pergunta de fato, não de julgamento.
 */

import { type CalendarDate, differenceInCalendarDays, weekdayOf } from './time.js';

/** Minuto local do fim do dia. Uma janela aberta vai de 0 a 1440. */
export const DAY_END_MINUTE = 1440;

export interface WaitlistWindow {
  /** 0 = domingo … 6 = sábado. **Vazio significa qualquer dia**, não nenhum. */
  weekdays: readonly number[];
  /** Minuto local a partir do qual a pessoa aceita. */
  earliestMinute: number;
  /** Minuto local até o qual o atendimento precisa terminar. */
  latestMinute: number;
  fromDate: CalendarDate;
  untilDate: CalendarDate;
}

/** A janela cobre este dia do calendário? */
export function coversDate(window: WaitlistWindow, date: CalendarDate): boolean {
  if (differenceInCalendarDays(date, window.fromDate) < 0) return false;
  if (differenceInCalendarDays(window.untilDate, date) < 0) return false;

  // Lista vazia é "tanto faz o dia". Tratar como "nenhum dia" transformaria a
  // espera mais comum de todas — a de quem aceita qualquer coisa — na única
  // que nunca casa.
  if (window.weekdays.length === 0) return true;

  return window.weekdays.includes(weekdayOf(date));
}

/**
 * Este horário concreto serve para esta espera?
 *
 * O atendimento **inteiro** precisa caber na faixa, não só o começo: quem disse
 * "das 9h ao meio-dia" está dizendo que ao meio-dia precisa ter ido embora, e
 * oferecer um corte de 40 minutos às 11h40 é não ter ouvido.
 */
export function acceptsStart(
  window: WaitlistWindow,
  date: CalendarDate,
  startMinute: number,
  durationMinutes: number,
): boolean {
  if (!coversDate(window, date)) return false;
  if (startMinute < window.earliestMinute) return false;
  return startMinute + durationMinutes <= window.latestMinute;
}

/** Uma espera já passou da validade neste dia? */
export function isExpired(window: WaitlistWindow, today: CalendarDate): boolean {
  return differenceInCalendarDays(today, window.untilDate) > 0;
}

/**
 * Dos horários livres do dia, os que servem para esta espera.
 *
 * Recebe os candidatos já calculados pela disponibilidade normal — a lista de
 * espera não inventa um segundo jeito de saber o que está livre. Se um dia a
 * regra de encaixe mudar, ela muda num lugar só e a espera acompanha de graça.
 */
export function matchingStarts(input: {
  window: WaitlistWindow;
  date: CalendarDate;
  durationMinutes: number;
  /** Minutos locais de início de cada horário livre. */
  candidates: readonly number[];
}): number[] {
  return input.candidates
    .filter((startMinute) =>
      acceptsStart(input.window, input.date, startMinute, input.durationMinutes),
    )
    .sort((a, b) => a - b);
}

export type WindowShape =
  /** Qualquer dia, qualquer hora — "me avisa se alguém cancelar". */
  | 'ANY'
  /** Só a partir de uma hora — "depois das 17h". */
  | 'AFTER'
  /** Só até uma hora — "até o meio-dia". */
  | 'BEFORE'
  /** Faixa dos dois lados — "entre 9h e 12h". */
  | 'BETWEEN';

/**
 * Que forma tem esta faixa de horário.
 *
 * Existe para a interface escrever "depois das 17h" em vez de "das 17:00 às
 * 24:00" — que é a mesma informação dita de um jeito que ninguém fala.
 */
export function windowShape(
  window: Pick<WaitlistWindow, 'earliestMinute' | 'latestMinute'>,
): WindowShape {
  const open = window.earliestMinute <= 0;
  const closed = window.latestMinute >= DAY_END_MINUTE;

  if (open && closed) return 'ANY';
  if (open) return 'BEFORE';
  if (closed) return 'AFTER';
  return 'BETWEEN';
}

/** Uma faixa de horário faz sentido? */
export function isValidWindowRange(earliestMinute: number, latestMinute: number): boolean {
  if (earliestMinute < 0 || latestMinute > DAY_END_MINUTE) return false;
  // Menos de meia hora de janela não é uma preferência, é um horário marcado —
  // e para isso existe o agendamento normal.
  return latestMinute - earliestMinute >= 30;
}
